import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import ProductCard from './ProductCard';

const FeaturedProductsCarousel = ({ products }) => {
  const [isPaused, setIsPaused] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);

  // Featured ürünleri filtrele (indirimli veya kampanyalı)
  const featuredProducts = products
    .filter(p => p.discount_percentage > 0 || p.is_featured)
    .slice(0, 8);

  // Auto-scroll
  useEffect(() => {
    if (isPaused || featuredProducts.length === 0) return;

    const interval = setInterval(() => {
      setCurrentIndex(prev => 
        prev >= featuredProducts.length - 4 ? 0 : prev + 1
      );
    }, 4000);

    return () => clearInterval(interval);
  }, [isPaused, featuredProducts.length]);

  // Scroll fonksiyonları
  const scroll = (direction) => {
    if (direction === 'left') {
      setCurrentIndex(prev => Math.max(0, prev - 1));
    } else {
      setCurrentIndex(prev => 
        Math.min(featuredProducts.length - 4, prev + 1)
      );
    }
  };

  if (featuredProducts.length === 0) return null;

  return (
    <div className="w-full bg-gradient-to-b from-purple-50 to-white py-16 my-12">
      <div className="max-w-7xl mx-auto px-4">
        {/* Başlık */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-2">
              ⭐ Öne Çıkan Ürünler
            </h2>
            <p className="text-gray-600">
              En çok tercih edilen ve indirimli ürünlerimiz
            </p>
          </div>
          
          <Link 
            to="/products"
            className="hidden md:flex items-center gap-2 text-brand-green hover:text-brand-green-dark font-semibold"
          >
            Tümünü Gör
            <ArrowRightIcon className="w-5 h-5" />
          </Link>
        </div>

        {/* Carousel */}
        <div 
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Navigation Buttons */}
          {featuredProducts.length > 4 && (
            <>
              <button
                onClick={() => scroll('left')}
                disabled={currentIndex === 0}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeftIcon className="w-6 h-6" />
              </button>
              <button
                onClick={() => scroll('right')}
                disabled={currentIndex >= featuredProducts.length - 4}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRightIcon className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Products Grid */}
          <div className="overflow-hidden" ref={carouselRef}>
            <div 
              className="grid grid-cols-2 md:grid-cols-4 gap-4 transition-transform duration-500"
              style={{ 
                transform: `translateX(-${currentIndex * 25}%)`
              }}
            >
              {featuredProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>

          {/* Indicators */}
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: Math.max(1, featuredProducts.length - 3) }).map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`h-2 rounded-full transition-all ${
                  currentIndex === idx 
                    ? 'bg-brand-green w-8' 
                    : 'bg-gray-300 w-2'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Mobile "Tümünü Gör" */}
        <div className="md:hidden text-center mt-6">
          <Link 
            to="/products"
            className="inline-flex items-center gap-2 text-brand-green hover:text-brand-green-dark font-semibold"
          >
            Tümünü Gör
            <ArrowRightIcon className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FeaturedProductsCarousel;
